/**
 * K108-X — owner/admin "Training Needs Review" queue model (pure).
 *
 * An item is unresolved when the technician's report differs from monitored
 * engagement (metadata.trainingAttestation.mismatch = 'user_attestation_mismatch')
 * and no owner/admin decision has been recorded yet (metadata.trainingReview
 * absent). Language is neutral — never accusatory, never "verified completion".
 */
import type { TrainingSessionMonitoringSummary } from '@/lib/training/trainingSessionMonitoringSummary';
import { ticketDisplayNumber } from '@/lib/tickets/ticketDisplayNumber';
import { historyObservedLine, userReportedLabel } from '@/lib/training/trainingHistoryCard';

export type ReviewAction = 'accept_report' | 'mark_partial' | 'request_retake' | 'dismiss';

export const REVIEW_ACTIONS: { action: ReviewAction; label: string; requiresNote: boolean }[] = [
  { action: 'accept_report', label: 'Accept user report', requiresNote: false },
  { action: 'mark_partial', label: 'Mark partially completed', requiresNote: false },
  { action: 'request_retake', label: 'Request retake / follow-up', requiresNote: true },
  { action: 'dismiss', label: 'Dismiss / no action needed', requiresNote: false },
];

export function reviewActionRequiresNote(action: ReviewAction): boolean {
  return REVIEW_ACTIONS.find((a) => a.action === action)?.requiresNote ?? false;
}

/** Minimal session shape the queue reads. */
export interface NeedsReviewSessionInput {
  id: string;
  ticket_id: string | null;
  opened_at: string | null;
  updated_at?: string | null;
  confirmed_at?: string | null;
  metadata: Record<string, unknown> | null;
  artifact: { id: string; title: string | null; provider_name?: string | null } | null;
  ticket?: { id: string; simple_ticket_number?: number | null; ticket_number?: string | null } | null;
  technicianProfile?: { id: string; full_name?: string | null } | null;
}

/** True when the session is an UNRESOLVED attestation mismatch (no review yet). */
export function isUnresolvedMismatch(session: NeedsReviewSessionInput): boolean {
  const meta = session.metadata ?? {};
  const att = meta['trainingAttestation'] as Record<string, unknown> | undefined;
  const hasMismatch = !!att && att.mismatch === 'user_attestation_mismatch';
  const alreadyReviewed = !!meta['trainingReview'];
  return hasMismatch && !alreadyReviewed;
}

export interface NeedsReviewItem {
  sessionId: string;
  technicianName: string;
  artifactTitle: string;
  provider: string | null;
  /** Raw ticket UUID — INTERNAL only, never displayed. */
  ticketId: string | null;
  /** Readable ticket number (e.g. "267"), never the UUID. */
  ticketDisplay: string | null;
  userReportedLabel: string | null;
  observedLine: string; // "OptiBay observed: 4% engagement" | "~N min" | "No monitored evidence available"
  monitoredPercent: number | null;
  confidence: 'low' | 'medium' | null;
  lastActivityAt: string | null;
  mismatchNote: string;
}

const MISMATCH_NOTE =
  'Needs review because the user report differs from monitored engagement. Monitoring may be incomplete, or the training may have been completed elsewhere.';

export function buildNeedsReviewItem(
  session: NeedsReviewSessionInput,
  summary: TrainingSessionMonitoringSummary | null,
): NeedsReviewItem {
  const att = (session.metadata ?? {})['trainingAttestation'] as Record<string, unknown> | undefined;
  const userReported = att && typeof att.userReported === 'string' ? (att.userReported as string) : null;
  const monitoredPercent = summary?.monitoredPercent ?? null;
  const evidenceUnavailable = summary?.evidenceUnavailable ?? true;

  return {
    sessionId: session.id,
    technicianName: session.technicianProfile?.full_name ?? 'Technician',
    artifactTitle: session.artifact?.title ?? 'Training',
    provider: session.artifact?.provider_name ?? null,
    ticketId: session.ticket_id ?? null,
    ticketDisplay: ticketDisplayNumber(session.ticket),
    userReportedLabel: userReportedLabel(userReported),
    observedLine: historyObservedLine(monitoredPercent, evidenceUnavailable, summary?.observedMinutes ?? null),
    monitoredPercent,
    confidence: summary?.confidence ?? null,
    lastActivityAt:
      summary?.lastActivityAt ?? session.confirmed_at ?? session.updated_at ?? session.opened_at ?? null,
    mismatchNote: MISMATCH_NOTE,
  };
}
